import React, { useState } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { toast } from 'react-hot-toast';

interface Milestone {
  id: string;
  title: string;
  dueDate: string;
  status: 'completed' | 'in_progress' | 'pending';
}

interface TeamMember {
  id: string;
  name: string;
  role: string;
  organization: string;
  avatar: string;
}

interface MilestoneForm {
  title: string;
  dueDate: string;
}

interface TeamMemberForm {
  name: string;
  role: string;
  organization: string;
}

const initialMilestoneForm: MilestoneForm = {
  title: '',
  dueDate: '',
};

const initialTeamMemberForm: TeamMemberForm = {
  name: '',
  role: '',
  organization: '',
};

export function ContractOverview() {
  const [milestones, setMilestones] = useState<Milestone[]>([
    {
      id: '1',
      title: 'Project Kickoff',
      dueDate: '2024-01-14',
      status: 'completed',
    },
    {
      id: '2',
      title: 'Requirements Analysis',
      dueDate: '2024-02-14',
      status: 'completed',
    },
    {
      id: '3',
      title: 'System Design',
      dueDate: '2024-03-14',
      status: 'in_progress',
    },
  ]);

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([
    {
      id: '1',
      name: 'Sarah Johnson',
      role: 'Contract Officer',
      organization: 'Department of Defense',
      avatar: 'https://ui-avatars.com/api/?name=Sarah+Johnson',
    },
    {
      id: '2',
      name: 'Michael Chen',
      role: 'Technical Lead',
      organization: 'Contractor',
      avatar: 'https://ui-avatars.com/api/?name=Michael+Chen',
    },
  ]);

  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [milestoneForm, setMilestoneForm] = useState<MilestoneForm>(initialMilestoneForm);
  const [memberForm, setMemberForm] = useState<TeamMemberForm>(initialTeamMemberForm);
  const [saving, setSaving] = useState(false);

  const handleAddMilestone = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const newMilestone: Milestone = {
        id: crypto.randomUUID(),
        title: milestoneForm.title,
        dueDate: milestoneForm.dueDate,
        status: 'pending',
      };

      setMilestones(prev => [...prev, newMilestone]);
      setShowMilestoneModal(false);
      setMilestoneForm(initialMilestoneForm);
      toast.success('Milestone added successfully');
    } catch (error) {
      toast.error('Failed to add milestone');
      console.error('Failed to add milestone:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const newMember: TeamMember = {
        id: crypto.randomUUID(),
        name: memberForm.name,
        role: memberForm.role,
        organization: memberForm.organization,
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(memberForm.name)}`,
      };

      setTeamMembers(prev => [...prev, newMember]);
      setShowMemberModal(false);
      setMemberForm(initialTeamMemberForm);
      toast.success('Team member added successfully');
    } catch (error) {
      toast.error('Failed to add team member');
      console.error('Failed to add team member:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Milestones Section */}
      <div>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">Milestones</h2>
          <button
            onClick={() => setShowMilestoneModal(true)}
            className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Milestone
          </button>
        </div>

        <div className="mt-4 space-y-4">
          {milestones.map((milestone) => (
            <div
              key={milestone.id}
              className="flex items-center justify-between rounded-lg border border-gray-200 p-4"
            >
              <div>
                <h3 className="font-medium text-gray-900">{milestone.title}</h3>
                <p className="text-sm text-gray-500">Due: {milestone.dueDate}</p>
              </div>
              <span
                className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  milestone.status === 'completed'
                    ? 'bg-green-100 text-green-800'
                    : milestone.status === 'in_progress'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {milestone.status.replace('_', ' ').charAt(0).toUpperCase() + milestone.status.slice(1)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Team Members Section */}
      <div>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">Team Members</h2>
          <button
            onClick={() => setShowMemberModal(true)}
            className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Member
          </button>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {teamMembers.map((member) => (
            <div
              key={member.id}
              className="flex items-center space-x-3 rounded-lg border border-gray-200 p-4"
            >
              <img
                src={member.avatar}
                alt={member.name}
                className="h-10 w-10 rounded-full"
              />
              <div>
                <h3 className="font-medium text-gray-900">{member.name}</h3>
                <p className="text-sm text-gray-500">{member.role}</p>
                <p className="text-sm text-gray-500">{member.organization}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Milestone Modal */}
      <Modal
        isOpen={showMilestoneModal}
        onClose={() => setShowMilestoneModal(false)}
        title="Add Milestone"
      >
        <form onSubmit={handleAddMilestone} className="space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
              Milestone Title
            </label>
            <input
              type="text"
              id="title"
              value={milestoneForm.title}
              onChange={(e) => setMilestoneForm({ ...milestoneForm, title: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700">
              Due Date
            </label>
            <input
              type="date"
              id="dueDate"
              value={milestoneForm.dueDate}
              onChange={(e) => setMilestoneForm({ ...milestoneForm, dueDate: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              required
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setShowMilestoneModal(false)}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Milestone
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Add Member Modal */}
      <Modal
        isOpen={showMemberModal}
        onClose={() => setShowMemberModal(false)}
        title="Add Team Member"
      >
        <form onSubmit={handleAddMember} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Full Name
            </label>
            <input
              type="text"
              id="name"
              value={memberForm.name}
              onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700">
              Role
            </label>
            <input
              type="text"
              id="role"
              value={memberForm.role}
              onChange={(e) => setMemberForm({ ...memberForm, role: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label htmlFor="organization" className="block text-sm font-medium text-gray-700">
              Organization
            </label>
            <input
              type="text"
              id="organization"
              value={memberForm.organization}
              onChange={(e) => setMemberForm({ ...memberForm, organization: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              required
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setShowMemberModal(false)}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Member
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}